import { useCallback, useMemo, useRef, useState } from 'react';
import { AxiosError, AxiosRequestConfig } from 'axios';

import { getFileUrl } from 'utils/file';
import { instance } from 'utils/api';

type Options = {
  progress?: boolean;
  loading?: boolean;
  handleError?: AxiosError;
  handleResult?: boolean;
};

const useFileUpload = (_options?: Options) => {
  const abortRef = useRef<Record<string, AbortController>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<string>('');

  const options: Options = useMemo(
    () => ({
      ..._options,
      progress: _options?.progress || false,
      handleError: _options?.handleError,
      handleResult: _options?.handleResult || false
    }),
    [_options]
  );

  const upload = useCallback(
    async (file: File, config: AxiosRequestConfig<FormData> = {}) => {
      if (options.loading) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setUploading(prev => ({ ...prev, [file?.uid]: true }));
      }

      const { onUploadProgress, ...rest } = config;

      const formData = new FormData();
      formData.append('file', file);

      const controller = new AbortController();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      abortRef.current[file?.uid] = controller;

      const res = await instance.post(`/aws-s3`, formData, {
        onUploadProgress(e) {
          const percent = Math.round((e.loaded * 100) / Number(e.total));

          onUploadProgress?.({
            progress: percent > 100 ? 100 : percent,
            loaded: e.loaded,
            bytes: e.bytes,
            lengthComputable: e.lengthComputable
          });
        },
        signal: controller.signal,
        ...rest
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete abortRef.current[file?.uid];

      if (options.handleResult) {
        setResult(res.data.data);
      }

      if (options.loading) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setUploading(prev => ({ ...prev, [file?.uid]: false }));
      }

      return res.data.data;
    },
    [options.handleResult, options.loading]
  );

  const fileUrl = useMemo(() => getFileUrl(result), [result]);

  const onCancel = useCallback((uid?: string) => {
    if (uid) {
      abortRef.current[uid]?.abort();
      delete abortRef.current[uid];
      setUploading(prev => ({ ...prev, [uid]: false }));
    }
  }, []);

  const loading = useMemo(
    () => Object.values(uploading).some(item => item),
    [uploading]
  );

  return {
    result,
    fileUrl,
    loading,
    setResult,
    onCancel,
    upload
  };
};

export default useFileUpload;
